import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon, type IconName } from "./Icon";
import "./tabs.css";

/**
 * Section tabs — the standard way any screen with more than ~2 sections is
 * organised (DESIGN_SYSTEM.md §4).
 *
 * Ground Truth had grown to five stacked sections (pillars, upload, documents,
 * claims, Ask Echo) and was a long scroll to reach anything. Tabs make the
 * page's structure visible at a glance and each section reachable in one click.
 *
 * The active tab is kept in the URL hash, which matters for three reasons: a
 * refresh doesn't dump you back on the first tab, a tab is linkable to a
 * colleague, and the browser back button behaves the way people expect.
 *
 * Keyboard behaviour follows the ARIA tabs pattern — arrow keys move between
 * tabs, Home/End jump to the ends — because a tablist that only responds to
 * clicks is worse than plain headings for a keyboard user.
 */

export interface TabDefinition {
  id: string;
  label: string;
  icon?: IconName;
  /** Optional count shown as a badge — e.g. how many claims need review. */
  badge?: number | string | null;
  render: () => ReactNode;
}

export function Tabs({
  tabs,
  ariaLabel,
  /**
   * Whether the active tab lives in the URL hash.
   *
   * True for a page's own tabs, where linkability and surviving a refresh are
   * the point. FALSE inside a side panel: the page underneath already owns the
   * hash, and two Tabs instances writing to it would fight — opening a panel
   * would silently switch the page's tab behind it. A panel's tab is also
   * ephemeral in a way a page's isn't; it dies with the panel.
   */
  urlSync = true,
  /** Visually tighter variant for use inside a panel. */
  compact = false,
}: {
  tabs: TabDefinition[];
  ariaLabel: string;
  urlSync?: boolean;
  compact?: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const baseId = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const fromHash = urlSync ? location.hash.replace(/^#/, "") : "";
  const initial = tabs.some((t) => t.id === fromHash)
    ? fromHash
    : (tabs[0]?.id ?? "");
  const [active, setActive] = useState(initial);

  // Keep state and URL in step when the hash changes from outside (back button,
  // a pasted link), without pushing a duplicate history entry.
  useEffect(() => {
    if (!urlSync) return;
    if (
      fromHash &&
      tabs.some((t) => t.id === fromHash) &&
      fromHash !== active
    ) {
      setActive(fromHash);
    }
  }, [urlSync, fromHash, tabs, active]);

  function select(id: string, focusIndex?: number) {
    setActive(id);
    if (urlSync) navigate({ hash: id }, { replace: true });
    if (focusIndex !== undefined) tabRefs.current[focusIndex]?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent, index: number) {
    const last = tabs.length - 1;
    let next: number | null = null;
    if (event.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    select(tabs[next]!.id, next);
  }

  const current = tabs.find((t) => t.id === active) ?? tabs[0];

  return (
    <div className={`tabs${compact ? " tabs--compact" : ""}`}>
      <div className="tabs__list" role="tablist" aria-label={ariaLabel}>
        {tabs.map((tab, index) => {
          const selected = tab.id === current?.id;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              // Roving tabindex: one stop for the whole tablist, then arrows.
              tabIndex={selected ? 0 : -1}
              className={`tabs__tab${selected ? " tabs__tab--active" : ""}`}
              onClick={() => select(tab.id)}
              onKeyDown={(e) => onKeyDown(e, index)}
            >
              {tab.icon && <Icon name={tab.icon} size={16} />}
              <span>{tab.label}</span>
              {tab.badge !== null &&
                tab.badge !== undefined &&
                tab.badge !== 0 && (
                  <span className="tabs__badge">{tab.badge}</span>
                )}
            </button>
          );
        })}
      </div>

      {current && (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${current.id}`}
          aria-labelledby={`${baseId}-tab-${current.id}`}
          tabIndex={0}
          className="tabs__panel"
        >
          {current.render()}
        </div>
      )}
    </div>
  );
}
